import { Component, EventEmitter, inject, Input, Output } from '@angular/core'
import { Call, CallResource, CallsService, GroupedAttendeeOrganization, MinutesStyling, MetricType, AgendaType, CallResourceType } from '../calls.service'
import { MatIcon } from '@angular/material/icon'
import { MinutesStylingComponent } from '../minutes-styling/minutes-styling.component'
import { AsyncPipe, DatePipe } from '@angular/common'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { MatTooltip } from '@angular/material/tooltip'
import { RouterLink } from '@angular/router'
import { CallDocumentUploadComponent, getMissingDocumentsForCall } from '../call-document-upload/call-document-upload.component'
import { UsersService } from '../users.service'
import { ToastService } from '../toast.service'
import { DemoProfilesService } from '../demo-profiles.service'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { FormsModule } from '@angular/forms'
import { MatOption } from '@angular/material/core'
import { MatSelect } from '@angular/material/select'
import { MatDialog } from '@angular/material/dialog'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { MetricsService } from '../metrics.service'
import { CallProfilesService } from '../call-profiles.service'
import { filter, map, switchMap } from 'rxjs'
import { MatButton } from '@angular/material/button'

@Component({
  selector: 'app-call-info',
  standalone: true,
  imports: [
    MatIcon,
    MinutesStylingComponent,
    DatePipe,
    TranslateModule,
    MatTooltip,
    RouterLink,
    CallDocumentUploadComponent,
    AsyncPipe,
    FormsModule,
    MatOption,
    MatFormField,
    MatSelect,
    MatLabel,
    MatButton,
  ],
  templateUrl: './call-info.component.html',
  styleUrl: './call-info.component.scss',
})
export class CallInfoComponent {
  @Input()
  call!: Call

  @Input()
  canEdit!: boolean

  @Output()
  boardPackRemoved = new EventEmitter<void>()

  @Output()
  agendaRemoved = new EventEmitter<void>()

  constructor() {}

  callsService = inject(CallsService)
  usersService = inject(UsersService)
  currentUser$ = this.usersService.currentUser$
  toastService = inject(ToastService)
  demoProfilesService = inject(DemoProfilesService)
  translateService = inject(TranslateService)
  metricsService = inject(MetricsService)
  callProfilesService = inject(CallProfilesService)
  dialog = inject(MatDialog)
  updateCallProfileMutation = this.callsService.updateCallProfileMutation()
  callProfiles$ = this.currentUser$.pipe(
    filter((result) => result !== null),
    map((result) => result.organizationId),
    switchMap((orgId) => this.callProfilesService.getCallProfilesByOrganizationId(orgId))
  )

  patchCallMutation = this.callsService.patchMutation()
  updateMediumMutation = this.callsService.updateMediumMutation()
  updateAgendaTypeMutation = this.callsService.updateAgendaTypeMutation()
  removeResourceMutation = this.callsService.removeResource()
  activeDemoProfile$ = this.demoProfilesService.activeDemoProfileId$
  reprocessOutputsMutation = this.callsService.reprocessOutputsMutation()

  getInviteeCount(groupedAttendees: GroupedAttendeeOrganization[]) {
    return groupedAttendees.reduce((acc, group) => acc + group.attendees.length, 0)
  }

  async updateCallProfile(id: number) {
    const callProfileId = id === 0 ? null : id
    if (!this.call.isPostProcessingComplete) {
      await this.updateCallProfileMutation.mutateAsync({ callId: this.call.id, data: { callProfileId } })
      this.toastService.show('CALLS.UPDATED')
      return
    }
    const previousValue = this.call.callProfileId
    this.call.callProfileId = callProfileId
    setTimeout(() => (this.call.callProfileId = previousValue), 0)
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.CALL_INFO.CHANGE_CALL_PROFILE_DIALOG.TITLE'),
        description: this.translateService.instant('CALLS.CALL_INFO.CHANGE_CALL_PROFILE_DIALOG.DESCRIPTION'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.updateCallProfileMutation.mutateAsync({ callId: this.call.id, data: { callProfileId } })
        this.toastService.show('CALLS.UPDATED')
      }
    })
  }

  async removeUpload(resource: CallResource) {
    await this.removeResourceMutation.mutateAsync({
      callId: this.call.id,
      resourceType: resource.resourceType,
      index: resource.index,
      extension: resource.extension,
    })
    this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.REMOVE_SUCCESS')
    if (resource.resourceType === 'BOARD_PACK') {
      this.boardPackRemoved.emit()
    } else if (resource.resourceType === 'AGENDA') {
      this.agendaRemoved.emit()
    }
  }

  get missingDocuments() {
    const missingDocuments = getMissingDocumentsForCall(this.call)
    if (this.call.agendaType === 'MULTIPLE') {
      return missingDocuments.filter((doc) => doc.type !== 'AGENDA')
    }
    return missingDocuments
  }

  async updateMinutesStyling(styling: Partial<MinutesStyling>) {
    await this.patchCallMutation.mutateAsync({ callId: this.call.id, data: styling })
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
    await this.metricsService.sendMetric(this.call.id, MetricType.CHANGE_MINUTING_STYLES, styling)
  }

  async updateAgendaType(agendaType: AgendaType) {
    await this.updateAgendaTypeMutation.mutateAsync({ callId: this.call.id, data: { agendaType } })
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
  }

  get isAuthorizedToManageDocuments() {
    return this.call.accessLevel === 'FULL_ACCESS' || this.call.accessLevel === 'EDITOR'
  }

  get isMediumSelectionDisabled() {
    return this.call.status === 'COMPLETED' && !this.call.isTranscriptionComplete
  }

  reprocessOutputs() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.REPROCESS_OUTPUTS.TITLE'),
        description: this.translateService.instant('CALLS.REPROCESS_OUTPUTS.DESCRIPTION', { callId: this.call.id }),
      },
    })

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.reprocessOutputsMutation.mutateAsync({ callId: this.call.id })
      }
    })
  }
}
